import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshItems, setStatus, setTransportDown, setUnreadCount } from '../features/notifications/notificationsSlice';
import { getUnreadCount, listNotifications } from '../utils/api';

const IDLE_INTERVAL_MS = 10000;
const OPEN_INTERVAL_MS = 5000;
const MAX_CONSECUTIVE_FAILURES = 3;

export function useNotificationPolling() {
  const dispatch = useDispatch();
  const panelOpen = useSelector((state) => state.notifications.panelOpen);
  const pollEpoch = useSelector((state) => state.notifications.pollEpoch);
  const userId = useSelector((state) => state.auth.user?.id);
  const failureCountRef = useRef(0);
  const pollIdRef = useRef(0);
  const pollEpochRef = useRef(pollEpoch);

  pollEpochRef.current = pollEpoch;

  useEffect(() => {
    if (!userId) return undefined;

    const poll = async () => {
      if (document.visibilityState !== 'visible') return;

      const pollId = ++pollIdRef.current;
      const startedEpoch = pollEpochRef.current;
      const isCurrent = () => pollId === pollIdRef.current && startedEpoch === pollEpochRef.current;

      try {
        const { unreadCount } = await getUnreadCount();
        if (!isCurrent()) return;
        dispatch(setUnreadCount(unreadCount));

        if (panelOpen) {
          const { items, nextCursor } = await listNotifications();
          if (!isCurrent()) return;
          dispatch(refreshItems({ items, nextCursor }));
          dispatch(setStatus('succeeded'));
        }

        failureCountRef.current = 0;
        dispatch(setTransportDown(false));
      } catch (error) {
        if (!isCurrent()) return;

        failureCountRef.current += 1;
        if (panelOpen) dispatch(setStatus('succeeded'));
        if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
          dispatch(setTransportDown(true));
        }
      }
    };

    poll();
    const intervalId = setInterval(poll, panelOpen ? OPEN_INTERVAL_MS : IDLE_INTERVAL_MS);

    return () => {
      pollIdRef.current += 1;
      clearInterval(intervalId);
    };
  }, [dispatch, panelOpen, userId]);
}
